import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgProgressModule } from '@ngx-progressbar/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DateInputsModule,
  DatePickerModule
} from '@progress/kendo-angular-dateinputs';
import { DialogModule, WindowModule } from '@progress/kendo-angular-dialog';
import {
  ComboBoxModule,
  DropDownsModule
} from '@progress/kendo-angular-dropdowns';
import { GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PopupModule } from '@progress/kendo-angular-popup';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { UploadModule } from '@progress/kendo-angular-upload';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { SelectModule } from 'ng2-select';
import { VixxoUIModule } from 'vixxo-ui';
import { AppFooterComponent } from '../app/components/footer/app-footer.component';
import { AppHeaderComponent } from '../app/components/header/app-header.component';
import { VixxolinkPermissionComponent } from '../app/components/vixxolink-permissions/vixxolink-permissions.component';
import { AuthService } from '../modules/security/service/auth.service';
import { B2CAuthService } from '../modules/security/service/b2cauth.service';
import { BulkAddProgressDialog } from './../modules/bulkAdd/shared/bulk-add-progress-dialog/bulk-add-progress-dialog.component';
import { PleaseCheckData } from './../modules/bulkAdd/shared/please-check-grid-dialog/please-check-grid-dialogue.component';
import { AdminPortalDatePickerComponent } from './components/admin-portal-date-picker/admin-portal-date-picker.component';
import { AssetAttachmentsComponent } from './components/asset-attachments/asset-attachments.component';
import { ButtonComponent } from './components/button/button.component';
import { ComboboxComponent } from './components/combobox/combobox.component';
import { ConfirmationComponent } from './components/confirmation/confirmation.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { DragDropComponent } from './components/drag-drop/drag-drop.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { IconComponent } from './components/icon/icon.component';
import { LinkedItemsListManageDialogComponent } from './components/linked-items-list-manage-dialog/linked-items-list-manage-dialog.component';
import { LinkedItemsListViewComponent } from './components/linked-items-list-view/linked-items-list-view.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MultiselectsComponent } from './components/multiselect/multiselects.component';
import { ResetPasswordModalComponent } from './components/reset-password-modal/reset-password-modal.component';
import { SwitchComponent } from './components/switch/switch.component';
import { ToolTipComponent } from './components/tool-tip/tooltip.component';
import { UIDialogDirective } from './directives/ui-dialog.directive';
import { UILoaderDirective } from './directives/ui-loader.directive';
import {
  UIPromptDirective,
  UIPromptService
} from './directives/ui-prompt.directive';
import { UIToastDirective } from './directives/ui-toast.directive';
import { en_DICTIONARY } from './i18n/en.dictionary';
import { CapitalizeStringPipe } from './pipes/capitalize-string.pipe';
import { TimeFromNowPipe } from './pipes/time-from-now.pipe';
import { TranslatePipe } from './pipes/translate.pipe';
import { UIBooleanPipe } from './pipes/types/ui-boolean.pipe';
import { ComponentCreatorService } from './services/component-creator.service';
import { NotifierService } from './services/notifier.service';
import { PreferenceService } from './services/preference.service';
import {
  UIActiveDialogService,
  UIDialogService
} from './services/ui-dialog.service';
import { UILoaderService } from './services/ui-loader.service';
import { VixxoApiService } from './services/vixxo-api.service';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgProgressModule,
    NgxDatatableModule,
    ReactiveFormsModule,
    SelectModule,
    VixxoUIModule,
    DropDownsModule,
    ComboBoxModule,
    DateInputsModule,
    ButtonsModule,
    InputsModule,
    UploadModule,
    LayoutModule,
    NgbModule,
    GridModule,
    TooltipModule,
    WindowModule,
    DialogModule,
    DatePickerModule
  ],
  declarations: [
    UILoaderDirective,
    UIToastDirective,
    UIBooleanPipe,
    AppHeaderComponent,
    VixxolinkPermissionComponent,
    AppFooterComponent,
    ButtonComponent,
    LoaderComponent,
    TranslatePipe,
    CapitalizeStringPipe,
    TimeFromNowPipe,
    UIDialogDirective,
    UIPromptDirective,
    LinkedItemsListViewComponent,
    LinkedItemsListManageDialogComponent,
    ConfirmationComponent,
    AdminPortalDatePickerComponent,
    SwitchComponent,
    DragDropComponent,
    PleaseCheckData,
    BulkAddProgressDialog,
    ToolTipComponent,
    ComboboxComponent,
    DropdownComponent,
    MultiselectsComponent,
    IconComponent,
    DateTimePickerComponent,
    ResetPasswordModalComponent,
    AssetAttachmentsComponent
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    GridModule,
    LayoutModule,
    UploadModule,
    NgbModule,
    NgxDatatableModule,
    SelectModule,
    VixxoUIModule,
    DropDownsModule,
    ComboBoxModule,
    DateInputsModule,
    ButtonsModule,
    PopupModule,
    AppHeaderComponent,
    VixxolinkPermissionComponent,
    AppFooterComponent,
    UILoaderDirective,
    UIToastDirective,
    UIDialogDirective,
    UIBooleanPipe,
    TranslatePipe,
    CapitalizeStringPipe,
    TimeFromNowPipe,
    LinkedItemsListViewComponent,
    LinkedItemsListManageDialogComponent,
    ConfirmationComponent,
    AdminPortalDatePickerComponent,
    SwitchComponent,
    ButtonComponent,
    LoaderComponent,
    DragDropComponent,
    PleaseCheckData,
    BulkAddProgressDialog,
    ToolTipComponent,
    ComboboxComponent,
    DropdownComponent,
    MultiselectsComponent,
    IconComponent,
    WindowModule,
    DialogModule,
    DateTimePickerComponent,
    ResetPasswordModalComponent,
    AssetAttachmentsComponent
  ],
  entryComponents: [
    LinkedItemsListManageDialogComponent,
    ConfirmationComponent,
    BulkAddProgressDialog,
    PleaseCheckData
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        AuthService,
        B2CAuthService,
        MsalService,
        BroadcastService,
        PreferenceService,
        NotifierService,
        UILoaderService,
        ComponentCreatorService,
        UIDialogService,
        UIActiveDialogService,
        UIPromptService,
        VixxoApiService,
        { provide: 'localizedDictionary', useValue: en_DICTIONARY }
      ]
    };
  }
}
